export class Chat {
  constructor() {
    this.id = 0
    this.caseId = 0
    this.title = ""
    this.description = ""
    this.createdAt = null
    this.updatedAt = null
    this.messages = []
    this.participants = []
    this.lastMessage = null
    this.unreadCount = 0
  }

  static FromObj(obj) {
    const instance = new Chat()

    instance.id = obj.id || 0
    instance.caseId = obj.caseId || 0
    instance.title = obj.title || ""
    instance.description = obj.description || ""
    instance.createdAt = obj.createdAt || null
    instance.updatedAt = obj.updatedAt || null
    instance.messages = (obj.messages || []).map((msg) =>
      ChatMessage.FromObj(msg)
    )
    instance.participants = obj.participants || []
    instance.lastMessage = obj.lastMessage
      ? ChatMessage.FromObj(obj.lastMessage)
      : null
    instance.unreadCount = obj.unreadCount || 0

    return instance
  }

  getDisplayTitle() {
    return this.title || `Chat do Caso #${this.caseId}`
  }

  hasUnreadMessages() {
    return this.unreadCount > 0
  }

  addMessage(message) {
    this.messages.push(message)
    this.lastMessage = message
    this.updatedAt = new Date()
  }

  getMessagesCount() {
    return this.messages.length
  }

  getParticipantsCount() {
    return this.participants.length
  }
}

export class ChatMessage {
  constructor() {
    this.id = 0
    this.chatId = 0
    this.userId = 0
    this.user = null
    this.message = ""
    this.messageType = "text" // text, image, file, system
    this.attachments = []
    this.createdAt = null
    this.updatedAt = null
    this.readBy = [] // Array de objetos { id, displayName, avatar, readAt } para usuários internos, ou array de IDs para legado
    this.edited = false
    this.editedAt = null
    this.deleted = false
    this.deletedAt = null
    this.replyToId = 0
    this.replyTo = null
  }

  static FromObj(obj) {
    const instance = new ChatMessage()

    instance.id = obj.id || 0
    instance.chatId = obj.chatId || 0
    instance.userId = obj.userId || 0
    instance.user = obj.user || null
    instance.message = obj.message || ""
    instance.messageType = obj.messageType || "text"
    instance.attachments = obj.attachments || []
    instance.createdAt = obj.createdAt || null
    instance.updatedAt = obj.updatedAt || null

    // Suportar tanto o formato novo (array de objetos) quanto o legado (array de IDs)
    if (obj.readBy && Array.isArray(obj.readBy)) {
      instance.readBy = obj.readBy
    } else {
      instance.readBy = []
    }

    instance.edited = obj.edited || false
    instance.editedAt = obj.editedAt || null
    instance.deleted = !!obj.isDeleted || !!obj.deleted || !!obj.deletedAt
    instance.deletedAt = obj.deletedAt || null
    instance.replyToId = obj.replyToId || 0
    instance.replyTo = obj.replyTo ? ChatMessage.FromObj(obj.replyTo) : null

    return instance
  }

  /**
   * Verifica se uma mensagem foi lida por um usuário específico
   * Suporta tanto o formato novo (array de objetos) quanto o legado (array de IDs)
   */
  isReadBy(userId) {
    if (!this.readBy || this.readBy.length === 0) return false

    // Formato novo: array de objetos { id, displayName, avatar, readAt }
    if (typeof this.readBy[0] === "object" && this.readBy[0] !== null) {
      return this.readBy.some((reader) => reader.id === userId)
    }

    // Formato legado: array de IDs
    return this.readBy.includes(userId)
  }

  /**
   * Marca a mensagem como lida por um usuário (formato legado)
   */
  markAsRead(userId) {
    if (!this.isReadBy(userId)) {
      this.readBy.push(userId)
    }
  }

  /**
   * Retorna o array de leitores no formato detalhado
   * Retorna array vazio se não estiver disponível (usuário não interno)
   */
  getDetailedReaders() {
    if (!this.readBy || this.readBy.length === 0) return []

    // Se já está no formato de objetos, retornar diretamente
    if (typeof this.readBy[0] === "object" && this.readBy[0] !== null) {
      return this.readBy
    }

    // Se for array de IDs, retornar vazio (não temos informações detalhadas)
    return []
  }

  /**
   * Retorna a contagem de leitores
   */
  getReadCount() {
    return this.readBy ? this.readBy.length : 0
  }

  /**
   * Verifica se a mensagem tem informações detalhadas de leitura
   * (disponível apenas para usuários internos)
   */
  hasDetailedReadInfo() {
    return (
      this.readBy &&
      this.readBy.length > 0 &&
      typeof this.readBy[0] === "object" &&
      this.readBy[0] !== null &&
      "displayName" in this.readBy[0]
    )
  }

  isEdited() {
    return this.edited
  }

  isDeleted() {
    return this.deleted
  }

  isSystemMessage() {
    return this.messageType === "system"
  }

  isTextMessage() {
    return this.messageType === "text"
  }

  isImageMessage() {
    return this.messageType === "image"
  }

  isFileMessage() {
    return this.messageType === "file"
  }

  hasAttachments() {
    return this.attachments.length > 0
  }

  isReply() {
    return this.replyToId > 0
  }

  getDisplayMessage() {
    if (this.isDeleted()) {
      return "Mensagem deletada"
    }

    if (this.isSystemMessage()) {
      return this.message
    }

    return this.message || "Mensagem sem conteúdo"
  }

  getFormattedDate() {
    if (!this.createdAt) return ""

    const date = new Date(this.createdAt)
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 1) {
      return (
        "Hoje às " +
        date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
      )
    } else if (diffDays === 2) {
      return (
        "Ontem às " +
        date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
      )
    } else if (diffDays <= 7) {
      return date.toLocaleDateString("pt-BR", {
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit"
      })
    } else {
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      })
    }
  }

  canEdit(userId) {
    return (
      this.userId === userId && !this.isDeleted() && !this.isSystemMessage()
    )
  }

  canDelete(isAdmin) {
    return isAdmin && !this.isDeleted()
  }

  canReply() {
    return !this.isDeleted() && !this.isSystemMessage()
  }
}
